import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit as qlimit,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { CATEGORIES, type Category } from '@sfp/shared/roles';
import { categoryLabel, formatTimestamp } from '../../lib/format.js';

interface TriageDoc {
  id: string;
  originalName?: string;
  category?: string;
  docType?: string;
  filingEntity?: string;
  llm?: { confidence?: number };
  uploadedAt?: { seconds: number };
}

/**
 * Triage queue — documents the classifier flagged low-confidence. Each row is
 * inline-editable: fix the category / doc type / entity, then Confirm (moves it
 * to 'ready' and out of the queue), or Re-run the AI classifier.
 */
export function TriageQueue() {
  const { role } = useAuth();
  const [docs, setDocs] = useState<TriageDoc[]>([]);
  const [entities, setEntities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const u1 = onSnapshot(
      query(
        collection(db, 'documents'),
        where('status', '==', 'triage'),
        orderBy('uploadedAt', 'desc'),
        qlimit(200),
      ),
      (snap) => {
        setDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TriageDoc, 'id'>) })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    const u2 = onSnapshot(query(collection(db, 'entities'), qlimit(300)), (snap) => {
      setEntities(
        snap.docs
          .map((d) => ((d.data() as { legalName?: string }).legalName ?? '').trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b)),
      );
    });
    return () => {
      u1();
      u2();
    };
  }, []);

  const isAdmin = role === 'admin';

  async function patch(id: string, fields: Partial<TriageDoc> & { status?: string }) {
    setBusyId(id);
    try {
      await updateDoc(doc(db, 'documents', id), {
        ...fields,
        reclassifiedManually: true,
        reclassifiedAt: serverTimestamp(),
      });
    } finally {
      setBusyId(null);
    }
  }

  async function reRunAi(id: string) {
    setBusyId(id);
    try {
      await updateDoc(doc(db, 'documents', id), { status: 'classifying' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-6 py-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Triage queue</h1>
        <p className="text-sm text-stone-500">
          Documents the classifier flagged as low-confidence. Edit the category, type, or entity if
          needed, then <strong>Confirm</strong> to file it — or re-run the AI.
        </p>
      </header>

      <div className="rounded-2xl border border-stone-200 bg-white shadow-sm">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Nothing in triage. Nice.</div>
        ) : !isAdmin ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Admins only.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-3 py-2">Document</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Doc type</th>
                <th className="px-3 py-2">Filing entity</th>
                <th className="px-3 py-2">Conf.</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-b border-stone-100 align-top last:border-b-0">
                  <td className="px-3 py-2">
                    <Link to={`/documents/${d.id}`} className="font-medium text-stone-800 hover:underline">
                      {d.originalName ?? d.id}
                    </Link>
                    <div className="mt-0.5 text-[10px] text-stone-400">{formatTimestamp(d.uploadedAt)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={d.category ?? 'other'}
                      disabled={busyId === d.id}
                      onChange={(e) => patch(d.id, { category: e.target.value as Category })}
                      className="w-full rounded border border-stone-300 px-1.5 py-1"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{categoryLabel(c)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      defaultValue={d.docType ?? ''}
                      disabled={busyId === d.id}
                      onBlur={(e) => {
                        if (e.target.value !== (d.docType ?? '')) patch(d.id, { docType: e.target.value });
                      }}
                      placeholder="doc type"
                      className="w-full rounded border border-stone-300 px-1.5 py-1"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      list="triage-entities"
                      defaultValue={d.filingEntity ?? ''}
                      disabled={busyId === d.id}
                      onBlur={(e) => {
                        if (e.target.value !== (d.filingEntity ?? '')) patch(d.id, { filingEntity: e.target.value.trim() });
                      }}
                      placeholder="entity"
                      className="w-full rounded border border-stone-300 px-1.5 py-1"
                    />
                  </td>
                  <td className="px-3 py-2 text-stone-500">
                    {d.llm?.confidence != null ? `${(d.llm.confidence * 100).toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      disabled={busyId === d.id}
                      onClick={() => patch(d.id, { status: 'ready' })}
                      className="rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      title="Accept this classification and file the document"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      disabled={busyId === d.id}
                      onClick={() => reRunAi(d.id)}
                      className="ml-2 rounded border border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                      title="Re-run the AI classifier on this document"
                    >
                      Re-run
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <datalist id="triage-entities">
        {entities.map((e) => (
          <option key={e} value={e} />
        ))}
      </datalist>
    </div>
  );
}
